<template>
    <div>
        <!-- Overlay -->
        <div
            v-if="visible"
            class="ppv2-fixed ppv2-inset-0 ppv2-bg-black ppv2-bg-opacity-50 ppv2-z-[9998]"
            @click="handleOverlayClick"
        ></div>

        <!-- Wrapper for sliding -->
        <transition name="panel-slide" v-if="visible">
            <div
                class="ppv2-absolute ppv2-top-0 ppv2-right-0 ppv2-h-full ppv2-z-[9999] ppv2-min-w-[450px] panel-responsive-width"
                :class="floating ? 'panel-floating' : ''"
                :style="width ? { '--panel-width': width } : {}"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="title ? 'panel-title' : null"
            >
                <!-- Actual panel -->
                <div class="ppv2-h-full ppv2-bg-gray-50 ppv2-shadow-xl ppv2-flex ppv2-flex-col panel-container">
                    <!-- Header -->
                    <div
                        class="ppv2-flex ppv2-items-start ppv2-justify-between ppv2-p-6 ppv2-border-b ppv2-border-gray-200 ppv2-bg-white"
                    >
                        <div class="ppv2-flex ppv2-flex-col ppv2-space-y-1 ppv2-flex-1 ppv2-pr-4">
                            <h2
                                v-if="title"
                                class="ppv2-text-2xl poppinsSemiBold ppv2-text-gray-900 ppv2-m-0-forced"
                                :id="'panel-title'"
                            >
                                {{ title }}
                            </h2>
                            <p
                                v-if="description"
                                class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-60 ppv2-m-0-forced ppv2-leading-normal"
                                v-html="description"
                            ></p>
                        </div>
                        <button
                            @click="close"
                            aria-label="Close panel"
                            class="panel-close-btn ppv2-flex ppv2-items-center ppv2-justify-center ppv2-w-8 ppv2-h-8 ppv2-rounded-lg ppv2-bg-gray-100 ppv2-text-gray-500 hover:ppv2-bg-gray-200 hover:ppv2-text-gray-700 ppv2-transition-colors ppv2-flex-shrink-0"
                        >
                            <svg class="ppv2-w-4 ppv2-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2.2"
                                    d="M6 18L18 6M6 6l12 12"
                                ></path>
                            </svg>
                        </button>
                    </div>

                    <!-- Content -->
                    <div
                        class="ppv2-flex-1 ppv2-overflow-auto ppv2-bg-white"
                        :style="contentPadding !== null ? { padding: contentPadding } : { padding: normalizeSize(24, true) }"
                    >
                        <slot></slot>
                    </div>

                    <!-- Footer -->
                    <div
                        v-if="footer"
                        class="ppv2-border-t ppv2-border-gray-200 ppv2-bg-white ppv2-p-4 ppv2-flex ppv2-justify-end ppv2-space-x-2"
                    >
                        <BaseButton @click="cancel" variant="outline">
                            {{ cancelTextComputed }}
                        </BaseButton>
                        <BaseButton @click="validate" :disabled="disabledConfirm" variant="primary">
                            {{ validateTextComputed }}
                        </BaseButton>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'
import BaseButton from './BaseButton.vue'

export default {
    name: 'PanelComponent',
    components: {
        BaseButton,
    },
    props: {
        title: { type: String, default: '' },
        description: { type: String, default: '' },
        width: { type: String, default: '50%' },
        visible: { type: Boolean, default: true },

        // Footer options
        footer: { type: Boolean, default: true },
        cancelText: { type: String, default: null },
        validateText: { type: String, default: null },

        disabledConfirm: { type: Boolean, default: false },
        contentPadding: { type: String, default: null },
        floating: { type: Boolean, default: false },
    },
    computed: {
        cancelTextComputed() {
            return this.cancelText || this.$t('common.cancel')
        },
        validateTextComputed() {
            return this.validateText || this.$t('common.confirm')
        },
    },
    methods: {
        normalizeSize,
        normalizeSizeVertical,
        close() {
            this.$emit('close') // used for explicit close (e.g. close button or cancel)
        },
        handleOverlayClick() {
            this.$emit('outside-close') // separate event for overlay click
        },
        cancel() {
            this.$emit('cancel')
            this.close()
        },
        validate() {
            this.$emit('validate')
        },
    },
}
</script>

<style scoped>
.panel-slide-enter-active,
.panel-slide-leave-active {
    transition:
        transform 0.3s ease-out,
        opacity 0.3s ease-out;
}

.panel-slide-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.panel-slide-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.ppv2-m-0-forced {
    margin: 0 !important;
}
</style>

<style>
/* Dark mode styles for PanelComponent */
.dark .panel-container {
    background-color: #141243 !important;
    box-shadow:
        inset 0 0 36px #2b3362,
        0 14px 45px rgba(0, 0, 0, 0.5) !important;
    border-left: 1.81px solid #5667a1 !important;
    position: relative;
}

.dark .panel-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/media/bo/backgounds/bgPopup.png');
    background-repeat: repeat;
    opacity: 0.02;
    z-index: 0;
    pointer-events: none;
}

.dark .panel-container > * {
    position: relative;
    z-index: 1;
}

.dark .panel-container .ppv2-bg-white,
.dark .panel-container .ppv2-bg-gray-50 {
    background-color: transparent !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
}

.dark .panel-container h2,
.dark .panel-container p,
.dark .panel-container span,
.dark .panel-container div {
    color: #fff !important;
}

.dark .panel-container button.ppv2-text-gray-400 {
    color: #fff !important;
}

.dark .panel-container button.ppv2-text-gray-400:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
}

.dark .panel-container .ppv2-border-gray-200 {
    border-color: rgba(255, 255, 255, 0.1) !important;
}

.dark .panel-container .panel-close-btn {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #fff !important;
}

.dark .panel-container .panel-close-btn:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
}
</style>
<style scoped>
@media (min-width: 768px) {
    .panel-responsive-width {
        width: var(--panel-width, 50%) !important;
        max-width: 650px !important;
    }
}
@media (max-width: 768px) {
    .panel-responsive-width {
        width: 100% !important;
    }
}

.panel-floating {
    border-radius: 14px 0 0 14px;
    overflow: hidden;
    box-shadow: -12px 0 32px rgba(0, 0, 0, 0.12);
}
</style>
